import { useTheme } from "@chakra-ui/react";
import { ReactElement } from "react";

interface BarProps {
  readonly value: number;
  readonly maxValue: number;
  readonly color?: string;
  readonly bgColor?: string;
  readonly height?: string;
}

export const SimpleBar: React.FC<BarProps> = ({
  value, // eslint-disable-line react/prop-types
  maxValue, // eslint-disable-line react/prop-types
  color = "#FF7A79", // eslint-disable-line react/prop-types
  bgColor = "#E5E5E5", // eslint-disable-line react/prop-types
  height = "24px", // eslint-disable-line react/prop-types
}) => {
  const theme = useTheme();
  const percentage = Math.round(Math.min((value / maxValue) * 100, 100));

  const containerStyle: React.CSSProperties = {
    width: "100%",
    backgroundColor: bgColor,
    height: height,
    fontSize: "20px",
    fontWeight: "bold",
    borderRadius: "4px",
    overflow: "hidden",
    textAlign: "center",
  };

  const barStyle: React.CSSProperties = {
    height: height,
    width: `${percentage}%`,
    backgroundColor: color,
    fontSize: "20px",
    fontWeight: "bold",
    color: theme.colors.white,
    textAlign: "center",
    transition: "width 0.5s ease-out",
  };

  return (
    <div style={{ width: "100%" }}>
      {/* If percentage is greater than 30, show the bar with the percentage value */}
      {percentage > 30 ? (
        <div style={containerStyle}>
          <div style={barStyle}>
            <div style={{ paddingTop: "2px" }}>{percentage}</div>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex" }}>
          <div style={barStyle} />
          <div style={{ ...containerStyle, width: 100 - percentage + "%" }}>
            <div style={{ paddingTop: "2px" }}>{percentage}</div>
          </div>
        </div>
      )}
    </div>
  );
};

interface BarData {
  value: number;
  color: string;
  label?: string;
}

interface HorizontalBarChartProps {
  data?: BarData[];
  width?: number; // eslint-disable-line react/prop-types
  height?: number;
  showLabels?: boolean;
  showValue?: boolean;
}

export const HorizontalBarChart: React.FC<
  Readonly<HorizontalBarChartProps>
> = ({
  // eslint-disable-next-line react/prop-types
  data = [
    { value: 25, color: "#58C0CE", label: "低い" },
    { value: 50, color: "#DED21E", label: "標準" },
    { value: 25, color: "#F78E2B", label: "高い" },
  ],
  width = 600, // eslint-disable-line react/prop-types
  height = 60, // eslint-disable-line react/prop-types
  showLabels = true, // eslint-disable-line react/prop-types
  showValue = false, // eslint-disable-line react/prop-types
}): ReactElement => {
  // 最大値を計算
  const maxValue = data.reduce((total, item) => total + item.value, 0);

  const totalWidth = width;
  // 各バーの幅を計算
  let currentX = 0;
  const bars = data.map((item) => {
    const barRate = item.value / maxValue;
    const barWidth = barRate * totalWidth;
    const bar = {
      ...item,
      x: currentX,
      width: barWidth,
      rate: barRate,
    };
    currentX += barWidth;
    return bar;
  });

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <svg
        width={width}
        height={height}
        style={{ maxWidth: "100%" }}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
      >
        {bars.map((bar, index) => (
          <rect
            key={index}
            x={bar.x}
            y={0}
            width={bar.width}
            height={height}
            fill={bar.color}
          />
        ))}
      </svg>
      {showValue && (
        <div
          style={{
            position: "absolute",
            left: bars[0].width / 2,
            top: 0,
            color: "black",
            padding: "2px 2px",
            fontSize: "20px",
            fontWeight: "normal",
          }}
        >
          {data[0].value}
        </div>
      )}
      {showLabels && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-evenly",
            width: "100%",
          }}
        >
          {data.map((item, index) => (
            <span key={index} className="text-center flex-1">
              {item.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
